import { 
  UserPersonalizationProfile, 
  RegionalFoodData, 
  PersonalizedFoodRecommendation 
} from '../types/users';

// Regional food database
const regionalFoodDatabase: { [countryCode: string]: RegionalFoodData } = {
  'US': {
    country: 'US',
    commonBreakfastItems: [
      'Scrambled eggs', 'Oatmeal', 'Pancakes', 'Cereal', 'Toast with peanut butter',
      'Yogurt with berries', 'Bagel with cream cheese', 'Smoothie bowl'
    ],
    commonLunchItems: [
      'Turkey sandwich', 'Caesar salad', 'Chicken wrap', 'Hamburger',
      'Grilled chicken salad', 'Soup and sandwich', 'Quinoa bowl'
    ],
    commonDinnerItems: [
      'Grilled chicken breast', 'Salmon with vegetables', 'Pasta with marinara',
      'Steak with potatoes', 'Stir-fry vegetables', 'Roasted turkey'
    ],
    commonSnacks: [
      'Apple slices', 'Mixed nuts', 'Greek yogurt', 'Granola bar',
      'Cheese and crackers', 'Hummus with vegetables'
    ],
    culturalDishes: ['BBQ ribs', 'Mac and cheese', 'Apple pie', 'Fried chicken'],
    seasonalFoods: {
      'spring': ['Asparagus', 'Spring onions', 'Strawberries'],
      'summer': ['Corn', 'Tomatoes', 'Watermelon', 'Peaches'],
      'fall': ['Pumpkin', 'Apples', 'Sweet potatoes'],
      'winter': ['Root vegetables', 'Citrus fruits', 'Winter squash']
    }
  },
  'IT': {
    country: 'IT',
    commonBreakfastItems: [
      'Cappuccino and cornetto', 'Espresso and biscotti', 'Yogurt with honey',
      'Fresh fruit', 'Brioche'
    ],
    commonLunchItems: [
      'Pasta with tomato sauce', 'Risotto', 'Pizza margherita', 'Minestrone soup',
      'Caprese salad', 'Panettone'
    ],
    commonDinnerItems: [
      'Osso buco', 'Chicken parmigiana', 'Seafood pasta', 'Grilled fish',
      'Eggplant parmigiana', 'Polenta'
    ],
    commonSnacks: [
      'Olives', 'Prosciutto and melon', 'Gelato', 'Nuts'
    ],
    culturalDishes: ['Carbonara', 'Tiramisu', 'Lasagna', 'Bruschetta'],
    seasonalFoods: {
      'spring': ['Artichokes', 'Peas', 'Asparagus'],
      'summer': ['Tomatoes', 'Basil', 'Zucchini'],
      'fall': ['Mushrooms', 'Chestnuts', 'Persimmons'],
      'winter': ['Oranges', 'Fennel', 'Radicchio']
    }
  },
  'MX': {
    country: 'MX',
    commonBreakfastItems: [
      'Huevos rancheros', 'Chilaquiles', 'Café de olla', 'Pan dulce',
      'Avocado toast', 'Fresh fruit with chili'
    ],
    commonLunchItems: [
      'Tacos', 'Quesadillas', 'Pozole', 'Enchiladas', 'Ceviche',
      'Black bean soup'
    ],
    commonDinnerItems: [
      'Chiles rellenos', 'Mole chicken', 'Carnitas', 'Fish tacos',
      'Beans and rice', 'Grilled carne asada'
    ],
    commonSnacks: [
      'Jicama with lime', 'Elote', 'Chicharrones', 'Fresh mango'
    ],
    culturalDishes: ['Mole poblano', 'Tamales', 'Guacamole', 'Flan'],
    seasonalFoods: {
      'spring': ['Nopales', 'Hibiscus flowers', 'Green chiles'],
      'summer': ['Mangoes', 'Coconut', 'Watermelon'],
      'fall': ['Pumpkin seeds', 'Persimmons', 'Pomegranates'],
      'winter': ['Oranges', 'Guavas', 'Sugar cane']
    }
  },
  'IN': {
    country: 'IN',
    commonBreakfastItems: [
      'Idli with sambar', 'Paratha with yogurt', 'Upma', 'Poha',
      'Dosa', 'Chai with biscuits'
    ],
    commonLunchItems: [
      'Dal rice', 'Chapati with vegetables', 'Biryani', 'Sambar rice',
      'Vegetable curry', 'Curd rice'
    ],
    commonDinnerItems: [
      'Chicken curry', 'Fish curry', 'Paneer masala', 'Mixed vegetables',
      'Rajma chawal', 'Tandoori chicken'
    ],
    commonSnacks: [
      'Samosa', 'Pakora', 'Namkeen', 'Fresh fruit', 'Lassi'
    ],
    culturalDishes: ['Butter chicken', 'Palak paneer', 'Masala dosa', 'Gulab jamun'],
    seasonalFoods: {
      'spring': ['Mango', 'Jackfruit', 'Bottle gourd'],
      'summer': ['Watermelon', 'Cucumber', 'Mint'],
      'fall': ['Pumpkin', 'Sweet potato', 'Pomegranate'],
      'winter': ['Oranges', 'Carrots', 'Spinach']
    }
  },
  'CA': {
    country: 'CA',
    commonBreakfastItems: [
      'Maple syrup pancakes', 'Canadian bacon and eggs', 'Oatmeal with maple syrup',
      'Tim Hortons coffee and donut', 'Bagel with cream cheese'
    ],
    commonLunchItems: [
      'Poutine', 'Montreal smoked meat sandwich', 'Butter tart', 'Caesar salad',
      'Tourtière', 'Split pea soup'
    ],
    commonDinnerItems: [
      'Salmon', 'Venison steak', 'Maple-glazed ham', 'Bannock bread',
      'Wild rice pilaf', 'Cod fish'
    ],
    commonSnacks: [
      'Nanaimo bars', 'Butter tarts', 'Canadian cheese', 'Maple cookies'
    ],
    culturalDishes: ['Poutine', 'Tourtière', 'Butter tart', 'Nanaimo bar'],
    seasonalFoods: {
      'spring': ['Fiddleheads', 'Rhubarb', 'Spring salmon'],
      'summer': ['Blueberries', 'Corn', 'Fresh salmon'],
      'fall': ['Apples', 'Cranberries', 'Wild game'],
      'winter': ['Root vegetables', 'Preserved fish', 'Maple syrup']
    }
  },
  'GB': {
    country: 'GB',
    commonBreakfastItems: [
      'Full English breakfast', 'Porridge', 'Beans on toast', 'Tea and biscuits',
      'Weetabix', 'Black pudding'
    ],
    commonLunchItems: [
      'Fish and chips', 'Shepherd\'s pie', 'Bangers and mash', 'Ploughman\'s lunch',
      'Jacket potato', 'Cornish pasty'
    ],
    commonDinnerItems: [
      'Roast beef and Yorkshire pudding', 'Chicken tikka masala', 'Steak and kidney pie',
      'Lancashire hotpot', 'Toad in the hole'
    ],
    commonSnacks: [
      'Digestive biscuits', 'Scones with clotted cream', 'Crisps', 'Shortbread'
    ],
    culturalDishes: ['Fish and chips', 'Shepherd\'s pie', 'Afternoon tea', 'Haggis'],
    seasonalFoods: {
      'spring': ['Lamb', 'Spring onions', 'Rhubarb'],
      'summer': ['Strawberries', 'New potatoes', 'Summer berries'],
      'fall': ['Apples', 'Game birds', 'Mushrooms'],
      'winter': ['Brussels sprouts', 'Parsnips', 'Winter stews']
    }
  }
};

export class PersonalizedFoodRecommendationEngine {
  
  generateRecommendations(
    profile: UserPersonalizationProfile,
    mealType: 'breakfast' | 'lunch' | 'dinner' | 'snack',
    timeOfDay?: string
  ): PersonalizedFoodRecommendation[] {
    const recommendations: PersonalizedFoodRecommendation[] = [];
    
    // Get regional foods
    const regionalData = regionalFoodDatabase[profile.country];
    if (regionalData) {
      recommendations.push(...this.getRegionalRecommendations(regionalData, mealType, profile));
    }
    
    // Add preference-based recommendations
    recommendations.push(...this.getPreferenceBasedRecommendations(profile, mealType));
    
    // Add health goal-specific recommendations
    recommendations.push(...this.getHealthGoalRecommendations(profile, mealType));
    
    // Add time-sensitive recommendations
    if (timeOfDay) {
      recommendations.push(...this.getTimeBasedRecommendations(timeOfDay, mealType, profile));
    }
    
    // Sort by confidence and return top recommendations
    return recommendations
      .sort((a, b) => b.confidence - a.confidence)
      .slice(0, 8);
  }
  
  private getRegionalRecommendations(
    regionalData: RegionalFoodData,
    mealType: 'breakfast' | 'lunch' | 'dinner' | 'snack',
    profile: UserPersonalizationProfile
  ): PersonalizedFoodRecommendation[] {
    const foods = this.getFoodsForMealType(regionalData, mealType);
    const currentSeason = this.getCurrentSeason();
    const seasonalFoods = regionalData.seasonalFoods[currentSeason] || [];
    
    return foods.map(food => ({
      foodName: food,
      category: 'regional',
      mealType,
      recommendationReason: `Popular ${mealType} in your region`,
      confidence: seasonalFoods.includes(food) ? 85 : 75,
      nutritionalBenefit: this.getNutritionalBenefit(food, profile),
      frequency: 'weekly'
    }));
  }
  
  private getPreferenceBasedRecommendations(
    profile: UserPersonalizationProfile,
    mealType: 'breakfast' | 'lunch' | 'dinner' | 'snack'
  ): PersonalizedFoodRecommendation[] {
    const recommendations: PersonalizedFoodRecommendation[] = [];
    
    // Based on favorite food categories
    profile.foodPreferences.favoriteCategories.forEach(category => {
      const foods = this.getFoodsForCategory(category, mealType);
      foods.forEach(food => {
        recommendations.push({
          foodName: food,
          category: 'preference',
          mealType,
          recommendationReason: `You enjoy ${category}`,
          confidence: 90,
          nutritionalBenefit: this.getNutritionalBenefit(food, profile),
          frequency: 'daily'
        });
      });
    });
    
    // Based on favorite cuisines
    profile.foodPreferences.favoriteCuisines.forEach(cuisine => {
      const foods = this.getFoodsForCuisine(cuisine, mealType);
      foods.forEach(food => {
        recommendations.push({
          foodName: food,
          category: 'cuisine',
          mealType,
          recommendationReason: `${cuisine} cuisine favorite`,
          confidence: 85,
          nutritionalBenefit: this.getNutritionalBenefit(food, profile),
          frequency: 'weekly'
        });
      });
    });
    
    return recommendations;
  }
  
  private getHealthGoalRecommendations(
    profile: UserPersonalizationProfile,
    mealType: 'breakfast' | 'lunch' | 'dinner' | 'snack'
  ): PersonalizedFoodRecommendation[] {
    const recommendations: PersonalizedFoodRecommendation[] = [];
    
    profile.healthGoals.forEach(goal => {
      const foods = this.getFoodsForHealthGoal(goal, mealType);
      foods.forEach(food => {
        recommendations.push({
          foodName: food,
          category: 'health',
          mealType,
          recommendationReason: `Supports ${goal.replace('_', ' ')}`,
          confidence: 88,
          nutritionalBenefit: this.getHealthGoalBenefit(food, goal),
          frequency: this.getFrequencyForHealthGoal(goal)
        });
      });
    });
    
    return recommendations;
  }
  
  private getTimeBasedRecommendations(
    timeOfDay: string,
    mealType: 'breakfast' | 'lunch' | 'dinner' | 'snack',
    profile: UserPersonalizationProfile
  ): PersonalizedFoodRecommendation[] {
    const hour = parseInt(timeOfDay.split(':')[0]);
    const recommendations: PersonalizedFoodRecommendation[] = [];
    
    // Morning energy boost
    if (hour >= 6 && hour <= 10 && mealType === 'breakfast') {
      recommendations.push({
        foodName: 'Oatmeal with berries',
        category: 'energy',
        mealType,
        recommendationReason: 'Perfect morning energy boost',
        confidence: 92,
        nutritionalBenefit: 'Sustained energy release, fiber for digestion',
        timeOfDay,
        frequency: 'daily'
      });
    }
    
    // Afternoon pick-me-up
    if (hour >= 14 && hour <= 16 && mealType === 'snack') {
      recommendations.push({
        foodName: 'Apple with almond butter',
        category: 'energy',
        mealType,
        recommendationReason: 'Natural afternoon energy boost',
        confidence: 88,
        nutritionalBenefit: 'Natural sugars, healthy fats, protein',
        timeOfDay,
        frequency: 'daily'
      });
    }
    
    return recommendations;
  }
  
  private getFoodsForMealType(
    regionalData: RegionalFoodData,
    mealType: 'breakfast' | 'lunch' | 'dinner' | 'snack'
  ): string[] {
    switch (mealType) {
      case 'breakfast': return regionalData.commonBreakfastItems;
      case 'lunch': return regionalData.commonLunchItems;
      case 'dinner': return regionalData.commonDinnerItems;
      case 'snack': return regionalData.commonSnacks;
      default: return [];
    }
  }
  
  private getFoodsForCategory(category: string, mealType: string): string[] {
    const categoryFoods: { [key: string]: string[] } = {
      'fruits': ['Apple', 'Banana', 'Orange', 'Berries', 'Mango'],
      'vegetables': ['Carrots', 'Broccoli', 'Spinach', 'Bell peppers', 'Tomatoes'],
      'grains': ['Oatmeal', 'Brown rice', 'Quinoa', 'Whole wheat bread', 'Barley'],
      'proteins': ['Chicken breast', 'Salmon', 'Eggs', 'Greek yogurt', 'Tofu'],
      'dairy': ['Milk', 'Cheese', 'Greek yogurt', 'Cottage cheese'],
      'nuts': ['Almonds', 'Walnuts', 'Cashews', 'Pistachios', 'Chia seeds']
    };
    
    return categoryFoods[category] || [];
  }
  
  private getFoodsForCuisine(cuisine: string, mealType: string): string[] {
    const cuisineFoods: { [key: string]: string[] } = {
      'mediterranean': ['Greek salad', 'Hummus', 'Olive tapenade', 'Grilled fish'],
      'asian': ['Miso soup', 'Stir-fry vegetables', 'Brown rice', 'Green tea'],
      'mexican': ['Black beans', 'Avocado', 'Salsa', 'Grilled vegetables'],
      'italian': ['Caprese salad', 'Minestrone soup', 'Whole grain pasta'],
      'indian': ['Dal', 'Vegetable curry', 'Chapati', 'Lassi']
    };
    
    return cuisineFoods[cuisine] || [];
  }
  
  private getFoodsForHealthGoal(goal: string, mealType: string): string[] {
    const healthGoalFoods: { [key: string]: string[] } = {
      'diabetes_management': [
        'Steel-cut oats', 'Quinoa', 'Leafy greens', 'Lean protein',
        'Nuts', 'Berries', 'Non-starchy vegetables', 'Greek yogurt',
        'Chia seeds', 'Lentils', 'Sweet potato', 'Cinnamon tea'
      ],
      'heart_health': [
        'Salmon', 'Avocado', 'Nuts', 'Olive oil', 'Oatmeal',
        'Dark leafy greens', 'Berries', 'Sardines', 'Flaxseeds',
        'Dark chocolate', 'Green tea', 'Garlic'
      ],
      'weight_loss': [
        'Lean protein', 'Vegetables', 'Fruits', 'Whole grains',
        'Legumes', 'Low-fat dairy', 'Egg whites', 'Cucumber',
        'Celery', 'Grapefruit', 'Green tea', 'Lean turkey'
      ],
      'muscle_building': [
        'Chicken breast', 'Eggs', 'Greek yogurt', 'Cottage cheese',
        'Tuna', 'Salmon', 'Quinoa', 'Lean beef', 'Protein smoothie',
        'Almonds', 'Sweet potato', 'Bananas'
      ],
      'digestive_health': [
        'Yogurt with probiotics', 'Kefir', 'Sauerkraut', 'Kimchi',
        'Bone broth', 'Ginger tea', 'Peppermint tea', 'Bananas',
        'Oatmeal', 'Papaya', 'Fennel'
      ],
      'brain_health': [
        'Blueberries', 'Walnuts', 'Fatty fish', 'Dark chocolate',
        'Turmeric tea', 'Green tea', 'Avocado', 'Broccoli',
        'Pumpkin seeds', 'Orange'
      ],
      'immune_support': [
        'Citrus fruits', 'Garlic', 'Ginger', 'Spinach', 'Yogurt',
        'Almonds', 'Turmeric', 'Green tea', 'Sweet potato',
        'Bell peppers', 'Kiwi'
      ],
      'cholesterol': [
        'Oatmeal', 'Beans', 'Nuts', 'Fatty fish', 'Vegetables',
        'Fruits', 'Whole grains'
      ]
    };
    
    return healthGoalFoods[goal] || [];
  }
  
  private getNutritionalBenefit(food: string, profile: UserPersonalizationProfile): string {
    const benefits: { [key: string]: string } = {
      'Oatmeal': 'High fiber, sustained energy, beta-glucan for heart health',
      'Salmon': 'Omega-3 fatty acids, high-quality protein, vitamin D',
      'Greek yogurt': 'Probiotics, protein, calcium for bone health',
      'Avocado': 'Healthy monounsaturated fats, fiber, potassium',
      'Quinoa': 'Complete protein, fiber, essential amino acids',
      'Berries': 'Antioxidants, vitamin C, fiber, low glycemic index'
    };
    
    return benefits[food] || 'Nutritious and well-balanced';
  }
  
  private getHealthGoalBenefit(food: string, goal: string): string {
    if (goal === 'diabetes_management') {
      return 'Low glycemic index, helps maintain stable blood sugar';
    }
    if (goal === 'heart_health') {
      return 'Supports cardiovascular health, reduces inflammation';
    }
    if (goal === 'weight_loss') {
      return 'High satiety, supports healthy weight management';
    }
    return 'Supports your health goals';
  }
  
  private getFrequencyForHealthGoal(goal: string): 'daily' | 'weekly' | 'occasional' {
    const criticalGoals = ['diabetes_management', 'heart_health'];
    return criticalGoals.includes(goal) ? 'daily' : 'weekly';
  }
  
  private getCurrentSeason(): string {
    const month = new Date().getMonth();
    if (month >= 2 && month <= 4) return 'spring';
    if (month >= 5 && month <= 7) return 'summer';
    if (month >= 8 && month <= 10) return 'fall';
    return 'winter';
  }
}

export const foodRecommendationEngine = new PersonalizedFoodRecommendationEngine(); 
